import React from "react";
import { graphql, useFragment, useLazyLoadQuery } from "react-relay";
import { AHText, Box, useLayoutContext, useTheme } from "arkh";
import { TeamScheduleListItem } from "../Team/TeamHomeNavigator/TeamScheduleListItem/TeamScheduleListItem";
import { TeamScheduleHistoryDetailTitle_teamSchedule$key } from "./__generated__/TeamScheduleHistoryDetailTitle_teamSchedule.graphql";
import { TeamScheduleHistoryDetailQuery } from "./__generated__/TeamScheduleHistoryDetailQuery.graphql";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { FlatList } from "react-native";
import { getTeamScheduleListItemHeight } from "../Team/TeamHomeNavigator/TeamScheduleListItem/getTeamScheduleListItemHeight";

function Title(props: {
  teamSchedule: TeamScheduleHistoryDetailTitle_teamSchedule$key;
}) {
  const teamSchedule = useFragment(
    graphql`
      fragment TeamScheduleHistoryDetailTitle_teamSchedule on TeamSchedule {
        id
        label
      }
    `,
    props.teamSchedule
  );

  return (
    <AHText spaceHorizontal="medium" spaceVertical="medium" type="title-big">
      {teamSchedule.label}
    </AHText>
  );
}

export const TeamScheduleHistoryDetail = React.memo(TeamScheduleHistoryDetailC);
function TeamScheduleHistoryDetailC(props: {
  id: string;
  fetchKey: string;
  teamId: string;
}) {
  const data = useLazyLoadQuery<TeamScheduleHistoryDetailQuery>(
    graphql`
      query TeamScheduleHistoryDetailQuery($id: ID!) {
        teamSchedule(id: $id) {
          id
          freq
          ...TeamScheduleHistoryDetailTitle_teamSchedule
          allDayEvents {
            id
            ...TeamScheduleListItem_TeamScheduleEventOccasion
          }
          events(includeInlineRoutines: true) {
            id
            ...TeamScheduleListItem_TeamScheduleEventOccasion
            ... on RoutineOccasion {
              __typename
              assigned {
                id
              }
              assignedRoles {
                id
              }
              routine {
                description
                descriptionImages {
                  __typename
                }
              }
            }
            ... on TeamScheduleInlineRoutineOccasion {
              __typename
              taskExecution {
                routineOccasion {
                  assigned {
                    id
                  }
                  assignedRoles {
                    id
                  }
                  routine {
                    description
                    descriptionImages {
                      __typename
                    }
                  }
                }
              }
            }
          }
        }
      }
    `,
    {
      id: props.id,
    },
    {
      fetchKey: props.fetchKey,
      fetchPolicy: "store-and-network",
    }
  );
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const layout = useLayoutContext();
  if (!data.teamSchedule) {
    return null;
  }
  return (
    <Box style={{ flex: 1 }}>
      <FlatList
        contentContainerStyle={{
          paddingBottom: insets.bottom + layout.space.xlarge,
        }}
        data={data.teamSchedule.events}
        keyExtractor={(item) => item.id}
        ListHeaderComponent={<Title teamSchedule={data.teamSchedule} />}
        renderItem={({ item, index }) => {
          const itemType = (() => {
            if (!data.teamSchedule) {
              return null;
            }
            if (data.teamSchedule.freq === "DAILY") {
              return "timeOfDay";
            }
            if (data.teamSchedule.freq === "WEEKLY") {
              return "WEEKLY";
            }
            if (data.teamSchedule.freq === "MONTHLY") {
              return "MONTHLY";
            }
            return null;
          })();

          if (!itemType) {
            return null;
          }

          const routineOccasion =
            item.__typename === "RoutineOccasion"
              ? item
              : item.taskExecution?.routineOccasion;

          const teamScheduleHeight = getTeamScheduleListItemHeight(
            itemType === "timeOfDay" ? "DAILY" : itemType,
            ((routineOccasion?.assigned?.length ?? 0) > 0 ||
              (routineOccasion?.assignedRoles?.length ?? 0) > 0) &&
              ((routineOccasion?.routine?.description?.length ?? 0) > 0 ||
                (routineOccasion?.routine?.descriptionImages?.length ?? 0) > 0)
          );
          return (
            <Box style={{ height: teamScheduleHeight }}>
              <TeamScheduleListItem
                itemType={itemType}
                status="none"
                isFirst={index === 0}
                isLast={data.teamSchedule?.events.length === index + 1}
                teamColor={theme.primary}
                eventOccasion={item}
              />
            </Box>
          );
        }}
      />
    </Box>
  );
}
